import React from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { ArrowTrendingUpIcon, PlusIcon } from '@heroicons/react/24/outline';
import { FORMA_LINK_BTN } from '../../utils/listPageUi';

export function DashboardStatCard({ stat, isLoading, detailsLabel }) {
  const Icon = stat.icon;

  return (
    <article className={clsx('dashboard-stat', stat.accent)}>
      <div className="flex items-start justify-between gap-3">
        <div className={clsx('dashboard-stat-icon', stat.iconWrap)}>
          <Icon className="h-5 w-5" aria-hidden />
        </div>
        <span
          className={clsx(
            'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium',
            stat.positive
              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
              : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
          )}
        >
          <ArrowTrendingUpIcon className={clsx('h-3 w-3', !stat.positive && 'rotate-180')} aria-hidden />
          {isLoading ? '…' : stat.change}
        </span>
      </div>
      <p className="mt-4 text-sm font-medium text-slate-500 dark:text-slate-400">{stat.title}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight text-slate-900 dark:text-white">
        {isLoading ? (
          <span className="inline-block h-8 w-20 animate-pulse rounded-md bg-slate-200 dark:bg-slate-700" />
        ) : (
          stat.value
        )}
      </p>
      <Link to={stat.link} className="dashboard-stat-link mt-4 inline-block text-sm">
        {detailsLabel}
      </Link>
    </article>
  );
}

export function DashboardPanel({ title, subtitle, actions, children, className }) {
  return (
    <section className={clsx('dashboard-panel', className)}>
      {(title || actions) && (
        <header className="dashboard-panel-head">
          <div className="min-w-0">
            {title && <h2 className="dashboard-panel-title">{title}</h2>}
            {subtitle && <p className="dashboard-panel-subtitle">{subtitle}</p>}
          </div>
          {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className="dashboard-panel-body">{children}</div>
    </section>
  );
}

export function DashboardDistributionCard({ item, pct, shareLabel }) {
  return (
    <div className="dashboard-dist">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h4 className="truncate text-sm font-medium text-slate-900 dark:text-white">{item.name}</h4>
        <span className="shrink-0 text-sm font-semibold tabular-nums text-slate-700 dark:text-slate-200">
          {item.count}
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        <div
          className={clsx('h-full rounded-full', item.barClass)}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{shareLabel}</p>
    </div>
  );
}

export function DashboardCourseRow({ course, language, viewLabel, studentsLabel }) {
  const formatted = new Date(course.startDate).toLocaleDateString(
    language === 'ar' ? 'ar-DZ' : 'fr-DZ',
    { day: 'numeric', month: 'short', year: 'numeric' }
  );

  return (
    <Link to={`/courses/${course.id}`} className="dashboard-course-row group">
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-slate-900 group-hover:text-forma-700 dark:text-white dark:group-hover:text-forma-400">
          {course.title}
        </p>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {formatted} · {course.students} {studentsLabel} · {course.room}
        </p>
      </div>
      <span className="dashboard-stat-link shrink-0 text-xs opacity-0 transition group-hover:opacity-100">
        {viewLabel}
      </span>
    </Link>
  );
}

export function DashboardEmptyState({ icon: Icon, title, body, actionLabel, actionHref }) {
  return (
    <div className="flex flex-col items-center px-4 py-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800">
        <Icon className="h-6 w-6 text-slate-500 dark:text-slate-400" aria-hidden />
      </div>
      <h3 className="mt-4 text-base font-semibold text-slate-900 dark:text-white">{title}</h3>
      <p className="mt-1.5 max-w-sm text-sm text-slate-600 dark:text-slate-400">{body}</p>
      {actionHref && actionLabel ? (
        <Link to={actionHref} className={clsx(FORMA_LINK_BTN, 'mt-5')}>
          <PlusIcon className="h-5 w-5" aria-hidden />
          {actionLabel}
        </Link>
      ) : null}
    </div>
  );
}
